import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { dataActionCreators, Invoice, InvoiceEntry, Sale, TimeSheetEntry } from '../../store/Data';
import { DataHelpers } from '../../DataHelpers';
import { GlobeShieldIcon, GlobeAddIcon, AddIcon, DocumentPdfIcon, useEditorStyles, TaskListLtrIcon, CodeBlockIcon } from '../Styles/Editor';
import { tokens, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Label, Link, Spinner, Textarea, TextareaOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController, Subtitle1, MessageBar, MessageBarBody, MessageBarTitle, Switch, SwitchOnChangeData } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { onFormatDate } from '../Tools/converters';
import { CustomerDropdown } from '../EditorHelpers/CustomerDropdown';
import { VATDropdown } from '../EditorHelpers/VATDropdown';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { FileViewer } from '../FileViewer/FileViewer';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { TimeSheets_Customer_NotInvoiced, TimeSheets_Customer_NotInvoicedByConsultant } from '../../API/Invoices';
import { useConsultants, useCustomers, useInvoices, useProjects, useProjectsAssigments, useRigths, useSales, useTimeSheetEntries } from '../Tools/ObjectLoader';
import { DigitealValidateDocument, DigitealValidateDocumentResp } from '../../API/Digiteal';
import { TimeSheetEntry_Sel, Sale_Sel } from './InvoicesEditor/Loaders';
import { InvoiceLine_ts } from './InvoicesEditor/InvoiceLine_ts';
import { InvoiceLine_sale } from './InvoicesEditor/InvoiceLine_sale';
import { InvoiceLine_free } from './InvoicesEditor/InvoiceLine_free';
import { v4 as uuidv4 } from 'uuid';
import { NIL as NIL_UUID } from 'uuid';
import { IsNeomyticConsultant } from '../../interfaces/Consultant';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragStartEvent,
    DragEndEvent,
    TouchSensor,
    closestCenter
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { IsEuropeIntracomCountry } from '../../interfaces/Countries';

// https://codesandbox.io/p/sandbox/react-typescript-with-dnd-kit-sortable-qk4y7x?file=%2Fsrc%2Fcomponents%2FItem.tsx%3A16%2C18-16%2C28

function SortTimeSheetEntry_Sel(a: TimeSheetEntry_Sel, b: TimeSheetEntry_Sel): number {
    const nameA = a.ts.timesheetEntryid ? a.ts.timesheetEntryid.toUpperCase() : ""; // ignore upper and lowercase
    const nameB = b.ts.timesheetEntryid ? b.ts.timesheetEntryid.toUpperCase() : ""; // ignore upper and lowercase
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}
function SortSale_Sel(a: Sale_Sel, b: Sale_Sel): number {
    const nameA = a.sale.sellid ? a.sale.sellid.toUpperCase() : ""; // ignore upper and lowercase
    const nameB = b.sale.sellid ? b.sale.sellid.toUpperCase() : ""; // ignore upper and lowercase
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}

export interface InvoiceEntry2 extends InvoiceEntry {
    id: number,
}

export const cutoff = new Date("2026-01-01T00:00:00Z");
export const InvoicesEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const data = useSelector((state: ApplicationState) => state.data);
    const teamsState = useSelector((state: ApplicationState) => state.teamsState);
    const { isAdmin } = useRigths();

    const { invoices } = useInvoices();
    const { customers, customersLoading } = useCustomers();
    const { projectsLoading } = useProjects();
    const { projectsAssigments, projectsAssigmentsLoading } = useProjectsAssigments();
    const { consultants, consultantsLoading } = useConsultants();
    const { sales, salesLoading } = useSales();
    const { timeSheetEntries, timeSheetEntriesLoading } = useTimeSheetEntries();

    const invoiceEdit = useSelector((state: ApplicationState) => state.data.invoiceEdit);
    const isCreditNote = invoiceEdit?.isCreditNote ?? false;
    const IsNeomyticInvoice = React.useMemo(() => {
        if (invoiceEdit) return IsNeomyticConsultant(invoiceEdit.consultantid);
        return false;
    }, [invoiceEdit]);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const consultant = React.useMemo(() => {
        if (consultants && invoiceEdit && invoiceEdit.consultantid) {
            return consultants.find(c => c.consultantid && c.consultantid.toUpperCase() === invoiceEdit.consultantid.toUpperCase());
        }
        return undefined;
    }, [invoiceEdit, consultants]);
    const [customer, setCustomer] = React.useState<Customer | null | undefined>(customers ? customers.find((c) => invoiceEdit && c.customerid === invoiceEdit.customerid) : undefined);
    const [invoiceRef, setInvoiceRef] = React.useState(invoiceEdit ? invoiceEdit.invoiceRef : "");
    const invoiceRefInputRef = React.useRef<HTMLInputElement>(null);
    const [date, setDate] = React.useState<Date | null>(invoiceEdit && invoiceEdit.invoiceDate ? new Date(invoiceEdit.invoiceDate) : null);
    const [paymentDate, setPaymentDate] = React.useState<Date | null>(invoiceEdit && invoiceEdit.paymentDate ? new Date(invoiceEdit.paymentDate) : null);
    const [payment, setPayment] = React.useState<number>(invoiceEdit ? invoiceEdit.payment : 0);
    const [lock, setLock] = React.useState<boolean>(invoiceEdit ? invoiceEdit.lock : false);
    const [vatCode, setVatCode] = React.useState<number>(invoiceEdit ? invoiceEdit.vatcode : 21);
    const [description, setDescription] = React.useState<string | null>(invoiceEdit ? invoiceEdit.description : null);
    const [buyerReference, setBuyerReference] = React.useState(invoiceEdit ? invoiceEdit.buyerReference : "0");
    const [invoiceDescription, setInvoiceDescription] = React.useState<string | null>(invoiceEdit ? invoiceEdit.invoiceDescription : null);
    const [prepaidAmount, setPrepaidAmount] = React.useState<number>(invoiceEdit && invoiceEdit.prepaidAmount ? invoiceEdit.prepaidAmount : 0);
    // Fields validation
    const [isInvoiceRefValid, setInvoiceRefValid] = React.useState(false);
    const [isInvoiceRefAllReadyExist, setInvoiceRefAllReadyExist] = React.useState(false);
    const [isCustomerValid, setCustomerValid] = React.useState(false);
    const [isDateValid, setDateValid] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const [file, setFile] = React.useState<Blob>();
    const [UBLfile, setUBLFile] = React.useState<Blob>();
    const [UBLValidate, setUBLValidate] = React.useState<DigitealValidateDocumentResp | null>();
    const [filePreview, setFilePreview] = React.useState<Blob>();

    const IsCustomerExemptFromTax = React.useMemo(() => {
        return customer && customer.vatexempt ? true : false;
    }, [customer]);
    const IsCustomerIntraCommunityTax = React.useMemo(() => {
        return customer && customer.countryCode && consultant && consultant.countryCode ? customer.countryCode.toUpperCase() !== consultant.countryCode.toUpperCase() && IsEuropeIntracomCountry(customer.countryCode) : false;
    }, [customer, consultant]);

    const [UBLGeneration, setUBLGeneration] = React.useState(false);

    //const isEditAndFile = React.useMemo(() => { return (invoiceEdit && invoiceEdit.invoiceid !== null && invoiceEdit.invoiceSharePointFileUniqueId) ?? false; }, [invoiceEdit]);
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    const iSReadOnly = React.useMemo(() => {
        if (viewDebugInfo) return false;
        return (invoiceEdit && invoiceEdit.invoiceid !== null && (invoiceEdit.emailSent !== null || invoiceEdit.peppolSent !== null)) ?? false;
    }, [invoiceEdit, viewDebugInfo]);

    const [timeSheetsEdition, setTimeSheetsEdition] = React.useState<TimeSheetEntry_Sel[] | null | undefined>(undefined);
    const timeSheetsInInvoice = React.useMemo((): TimeSheetEntry_Sel[] => {
        // Retrieve the TS already associated with the invoice
        if (invoiceEdit && timeSheetEntries && !timeSheetEntriesLoading) {
            if (IsNeomyticInvoice && invoiceEdit.timeSheetEntryInvoices) {
                return invoiceEdit.timeSheetEntryInvoices.map((tsid): TimeSheetEntry_Sel => {
                    const t = timeSheetEntries.find(c => c.timesheetEntryid === tsid);
                    return {
                        isSelected: true,
                        ts: t as TimeSheetEntry,
                    };
                });
            }
            else if (invoiceEdit.timeSheetEntryConsultantInvoices) {
                return invoiceEdit.timeSheetEntryConsultantInvoices.map((tsid): TimeSheetEntry_Sel => {
                    const t = timeSheetEntries.find(c => c.timesheetEntryid === tsid);
                    return {
                        isSelected: true,
                        ts: t as TimeSheetEntry,
                    };
                });
            }
        }
        return [];
    }, [invoiceEdit, timeSheetEntries, timeSheetEntriesLoading]);
    const [timeSheetsNotInvoiced, setTimeSheetsNotInvoiced] = React.useState<TimeSheetEntry_Sel[] | null | undefined>(undefined);
    const existingSales = React.useMemo((): Sale_Sel[] => {
        // Retrieve the sales already associated with the invoice
        if (invoiceEdit && invoiceEdit.sellEntries && sales && !salesLoading) {
            return invoiceEdit.sellEntries.map((sid): Sale_Sel => {
                const s = sales.find(c => c.sellid === sid);
                return {
                    isSelected: true,
                    sale: s as Sale,
                };
            });
        }
        return [];
    }, [invoiceEdit, sales, salesLoading]);
    const sales2 = React.useMemo(() => {
        if (customer && customer.customerid && sales && sales.length > 0) {
            return sales.filter(s => s.invoiceid === null && customer && s.customerid === customer.customerid).map((s): Sale_Sel => ({ sale: s, isSelected: false }));
        }
    }, [sales, customer]);
    const [salesConcat, setSalesConcat] = React.useState<Sale_Sel[] | null | undefined>(undefined);

    const [invoiceEntries, setInvoiceEntries] = React.useState<InvoiceEntry2[]>(invoiceEdit && invoiceEdit.invoiceEntries ? invoiceEdit.invoiceEntries.map((ie, index) => ({ ...ie, id: ie.orderNumber })) : []);

    const areInvoiceEntriesOK = (): boolean => {
        return invoiceEntries.filter(ie => ie.description === null || ie.description.length === 0).length === 0;
    };

    React.useEffect(() => {
        if (IsNeomyticInvoice && consultant && invoices && invoiceEdit && invoiceEdit.invoiceid === null) {
            // in the invoices list, find the last invoice ref number for neomytic consultant and increment it by one (the format must be "2026/0001" for example)
            const neomyticInvoices = invoices.filter(i => i.consultantid === consultant.consultantid);
            if (neomyticInvoices.length > 0) {
                const lastInvoiceRef = neomyticInvoices.map(i => i.invoiceRef).filter(ref => ref && ref.startsWith(new Date().getFullYear().toString() + "/")) // filter the invoice ref with the current year
                    .map(ref => parseInt(ref!.split("/")[1])) // get the number part of the invoice ref
                    .filter(num => !isNaN(num)) // filter the invoice ref with number part not a number
                    .sort((a, b) => b - a)[0]; // get the last invoice ref number
                const newInvoiceRef = (new Date().getFullYear().toString() + "/" + ((lastInvoiceRef ? lastInvoiceRef : 0) + 1).toString().padStart(4, "0"));
                setInvoiceRef(newInvoiceRef);
            }
        }
    }, [IsNeomyticInvoice, invoiceEdit, consultant, invoices]);

    const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

    const handleDragStart = (event: DragStartEvent) => { };

    // triggered when dragging ends
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;

        const activeItem = invoiceEntries.find((item) => item.id === active.id);
        const overItem = invoiceEntries.find((item) => item.id === over.id);

        if (!activeItem || !overItem) {
            return;
        }

        const activeIndex = invoiceEntries.findIndex((item) => item.id === active.id);
        const overIndex = invoiceEntries.findIndex((item) => item.id === over.id);

        if (activeIndex !== overIndex) {
            setInvoiceEntries((prev) => arrayMove<InvoiceEntry2>(prev, activeIndex, overIndex));
        }
    };

    const handleDragCancel = () => { };

    React.useEffect(() => {
        invoiceRefInputRef.current?.focus();
    }, []);
    React.useEffect(() => {
        let tss: TimeSheetEntry_Sel[] = [];
        if (IsNeomyticInvoice) {
            if (timeSheetsInInvoice && timeSheetsInInvoice.length > 0) { tss = [...tss, ...timeSheetsInInvoice]; }
            if (!iSReadOnly && timeSheetsNotInvoiced && timeSheetsNotInvoiced.length > 0) { tss = [...tss, ...timeSheetsNotInvoiced]; }
        }
        else {
            if (!iSReadOnly) {
                tss = timeSheetEntries && consultant && projectsAssigments
                    ?
                    timeSheetEntries.filter(ts => {
                        if (ts.consultantInvoiceid || (ts.date && new Date(ts.date) < cutoff)) {
                            return false;
                        }
                        const pa = projectsAssigments.find(x => x.projectAssignmentid === ts.projectAssignmentid);
                        if (pa && pa.consultantid === consultant.consultantid && pa.consultantPrice !== 0) {
                            return true;
                        }
                        return false;
                    }).map((s): TimeSheetEntry_Sel => ({ ts: s, isSelected: false }))
                    :
                    [];
            }
            if (timeSheetsInInvoice && timeSheetsInInvoice.length > 0) { tss = [...tss, ...timeSheetsInInvoice]; }
        }
        setTimeSheetsEdition(tss.sort(SortTimeSheetEntry_Sel));
    }, [timeSheetsInInvoice, timeSheetsNotInvoiced, timeSheetEntries, consultant, projectsAssigments, IsNeomyticInvoice, iSReadOnly]);
    React.useEffect(() => {
        let s: Sale_Sel[] = [];
        if (existingSales && existingSales.length > 0) { s = [...s, ...existingSales]; }
        if (sales2 && sales2.length > 0) { s = [...s, ...sales2]; }
        setSalesConcat(s.sort(SortSale_Sel));
    }, [existingSales, sales2]);

    React.useEffect(() => {
        if (invoiceEdit && invoiceEdit.invoiceid !== null && invoiceEdit.invoiceSharePointFileUniqueId)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && invoiceEdit && invoiceEdit.invoiceid) {
                        DownloadFile('api/Invoices/' + invoiceEdit.invoiceid + "/DownloadInvoiceFile", token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch((e) => { console.log(e); });
                    }
                });
    }, [invoiceEdit]);

    React.useEffect(() => {
        if (!iSReadOnly) {
            if (IsNeomyticInvoice) {
                if (customer && customer.customerid) {
                    TokenHelper.GetToken(instance, teamsState)
                        .then(token => {
                            if (token !== null && customer && customer.customerid) {
                                TimeSheets_Customer_NotInvoiced(token, customer.customerid)
                                    .then((f) => {
                                        console.log(f);
                                        if (f) setTimeSheetsNotInvoiced(f.map((s): TimeSheetEntry_Sel => ({ ts: s, isSelected: false })));
                                    })
                                    .catch((e) => { console.log(e); });
                            }
                        });
                }
            }
            else {
                if (consultant && consultant.consultantid) {
                    TokenHelper.GetToken(instance, teamsState)
                        .then(token => {
                            if (token !== null && consultant && consultant.consultantid) {
                                TimeSheets_Customer_NotInvoicedByConsultant(token, consultant.consultantid)
                                    .then((f) => {
                                        if (f) setTimeSheetsNotInvoiced(f.map((s): TimeSheetEntry_Sel => ({ ts: s, isSelected: false })));
                                    })
                                    .catch((e) => { console.log(e); });
                            }
                        });
                }
            }
        }
    }, [instance, teamsState, iSReadOnly, customer, consultant]);

    const InvoiceTotal = React.useMemo(() => {
        return invoiceEdit && invoiceEdit.invoiceid !== null && customer && consultant ? DataHelpers.GetInvoiceTotalFromInvoice(data, consultant, customer, invoiceEdit) : undefined;
    }, [data, invoiceEdit, customer, consultant]);
    React.useEffect(() => {
        const b: boolean = invoiceRef !== undefined && invoiceRef !== null && invoiceRef.length > 0;
        if (b && data.invoices) {
            // since an invoice ref must be unique, check
            const i = invoices?.filter((i) => i.consultantid === consultant?.consultantid).findIndex(i => i.invoiceRef === invoiceRef && invoiceEdit && i.invoiceid !== invoiceEdit.invoiceid);
            setInvoiceRefAllReadyExist(i !== -1);
        } else {
            setInvoiceRefAllReadyExist(false);
        }
        setInvoiceRefValid(b);
    }, [invoiceRef]);
    React.useEffect(() => {
        setDateValid(date !== null);
    }, [date]);
    React.useEffect(() => {
        setCustomerValid(customer !== null && customer !== undefined);
    }, [customer]);
    React.useEffect(() => {
        const submitValid =
            isCustomerValid
            && isInvoiceRefValid
            && !isInvoiceRefAllReadyExist
            && isDateValid
            && (areInvoiceEntriesOK() || (timeSheetsEdition !== undefined && timeSheetsEdition !== null && timeSheetsEdition.length > 0) || (salesConcat !== undefined && salesConcat !== null && salesConcat.length > 0))
            ;
        //if (submitValid && data && data.customerEdit) {
        //    // If valid, check if any change on existing item
        //    submitValid = submitValid && (
        //        displayName !== data.customerEdit.displayName
        //        || name !== data.customerEdit.name
        //        || vat !== data.customerEdit.vat
        //        || address != data.customerEdit.address
        //        || postalCode != data.customerEdit.postalCode
        //        || city != data.customerEdit.city
        //        || country != data.customerEdit.country
        //    );
        //}
        setSubmitValid(submitValid);
    }, [isCustomerValid, isInvoiceRefValid, isInvoiceRefAllReadyExist, isDateValid, invoiceEntries, timeSheetsEdition, salesConcat]);

    const save = async () => {
        const tsids: string[] = timeSheetsEdition ? timeSheetsEdition.filter(ts => ts.isSelected).map(ts => ts.ts.timesheetEntryid ?? "") : [];
        const sids: string[] = salesConcat ? salesConcat.filter(s => s.isSelected).map(s => s.sale.sellid ?? "") : [];
        //console.log("ies", invoiceEntries.map((ie, index): InvoiceEntry => ({ ...ie, orderNumber: index })));
        const c: Invoice = {
            invoiceid: invoiceEdit && invoiceEdit.invoiceid ? invoiceEdit.invoiceid : null,
            customerid: customer ? customer.customerid : null,
            invoiceRef: invoiceRef,
            invoiceDate: ConvertionHelpers.DateToISODateOnly(date),
            paymentDate: ConvertionHelpers.DateToISODateOnly(paymentDate),
            payment: payment,
            invoiceSharePointFileUniqueId: null,
            vatcode: vatCode,
            description: description,
            invoiceDescription: invoiceDescription,
            buyerReference: buyerReference,
            consultantid: invoiceEdit && invoiceEdit.consultantid ? invoiceEdit.consultantid : "",
            pdfuploaded: invoiceEdit && invoiceEdit.pdfuploaded ? invoiceEdit.pdfuploaded : false,
            prepaidAmount: prepaidAmount === 0 ? null : prepaidAmount,
            invoiceEntries: invoiceEntries.map((ie, index): InvoiceEntry => ({ ...ie, orderNumber: index })),
            timeSheetEntryInvoices: IsNeomyticInvoice ? tsids : [],
            timeSheetEntryConsultantInvoices: IsNeomyticInvoice ? [] : tsids,
            sellEntries: sids ? sids : [],
            lock: lock,
            isCreditNote: isCreditNote,
        };

        const token = await TokenHelper.GetToken(instance, teamsState);

        const toastId = (Math.random() + 1).toString(36).substring(7);
        dispatchToast(
            <Toast appearance="inverted">
                <ToastTitle>{"Saving invoice " + c.invoiceRef + "..."}</ToastTitle>
            </Toast>,
            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
        );
        if (token !== null) {
            console.log("Save Invoice");
            const response = await fetch("api/Invoices/" + (invoiceEdit && invoiceEdit.invoiceid !== null ? invoiceEdit.invoiceid : ""), {
                method: invoiceEdit && invoiceEdit.invoiceid === null ? "POST" : "PUT",  // if customerid exist, path
                headers: {
                    "Content-Type": "application/json",
                    authorization: "bearer " + token,
                },
                body: JSON.stringify(c),
                mode: "cors",
                cache: "default",
            });
            if (response.status === 200) {
                updateToast({
                    content: (
                        <Toast appearance="inverted">
                            <ToastTitle action={
                                <ToastTrigger>
                                    <Link>Dismiss</Link>
                                </ToastTrigger>
                            }>
                                {c.invoiceRef ? ("Invoice " + c.invoiceRef + " saved") : "Invoice saved"}
                            </ToastTitle>
                        </Toast>
                    ),
                    intent: "success",
                    toastId,
                    timeout: 5000,
                });
                const data: Invoice = await (response.json() as Promise<Invoice>);
                if (data) {
                    console.log("Save Invoice get", data);
                    dispatch(dataActionCreators.patchInvoiceEdition(data));
                    dispatch(dataActionCreators.requestInvoiceEdition(data));
                }
            }
            else {
                updateToast({
                    content: (
                        <Toast appearance="inverted">
                            <ToastTitle action={
                                <ToastTrigger>
                                    <Link>Dismiss</Link>
                                </ToastTrigger>
                            }>
                                {c.invoiceRef ? ("Invoice " + c.invoiceRef + " save failed") : "Invoice save failed"}
                            </ToastTitle>
                        </Toast>
                    ),
                    intent: "error",
                    toastId,
                    timeout: 10000,
                });
            }
        }
    };

    const submit = () => {
        save().then(() => {
            //dispatch(dataActionCreators.requestInvoiceEdition(null));
        });
    };

    const updateFreeLine = (id: string, patch: Partial<InvoiceEntry>) => {
        setInvoiceEntries((prev) => prev.map((l) => (l.invoiceEntryid === id ? { ...l, ...patch } : l)));
    };
    const deleteFreeLine = (id: string) => {
        setInvoiceEntries((prev) => prev ? prev.filter((l) => l.invoiceEntryid !== id) : []);
    };
    const GenBuyerRef = (ts: TimeSheetEntry_Sel[] | null | undefined, sc: Sale_Sel[] | null | undefined) => {
        let l: string[] = [];
        if (ts && ts.length > 0) {
            l = l.concat(ts
                .filter(ts => ts.isSelected)
                .map(ts => {
                    if (projectsAssigments) {
                        const pa = projectsAssigments.find(x => x.projectAssignmentid === ts.ts.projectAssignmentid);
                        if (pa && pa.po) {
                            return pa.po.trim();
                        }
                    }
                    return "";
                })
            );
        }
        if (sc && sc.length > 0) {
            l = l.concat(sc
                .filter(ts => ts.isSelected)
                .map(ts => ts.sale.orderRef && ts.sale.orderRef.trim() !== "" ? ts.sale.orderRef.trim() : "")
            );
        }
        setBuyerReference([...new Set(l.filter(s => s && s.trim() !== ""))].join("/"));
    };
    const tsSelectionChange = (id: string, selected: boolean) => {
        if (IsNeomyticInvoice) {
            const ts = timeSheetsEdition ? timeSheetsEdition.map((l) => (l.ts.timesheetEntryid === id ? { ... { ts: l.ts, isSelected: selected } } : l)) : [];
            setTimeSheetsEdition(ts);
            // if (selected) {
            //     setSalesConcat((prev) => prev ? prev.map((l) => ({ sale: l.sale, isSelected: false })) : undefined);
            // }
            GenBuyerRef(ts, salesConcat);
        }
        else {
            setTimeSheetsEdition((prev) => prev ? prev.map((l) => (l.ts.timesheetEntryid === id ? { ... { ts: l.ts, isSelected: selected } } : l)) : []);
        }
    };
    const saleSelectionChange = (id: string, selected: boolean) => {
        const s = salesConcat ? salesConcat.map((l) => (l.sale.sellid === id ? { ... { sale: l.sale, isSelected: selected } } : l)) : [];
        setSalesConcat(s);
        GenBuyerRef(timeSheetsEdition, s);
        // if (selected) {
        //     setTimeSheetsEdition((prev) => prev ? prev.map((l) => ({ ts: l.ts, isSelected: false })) : undefined);
        // }
    };
    const GenUBL = () => {
        setUBLGeneration(true);
        setUBLFile(undefined);
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null && invoiceEdit && invoiceEdit.invoiceid) {
                    console.log("download UBL");
                    DownloadFile('api/Invoices/' + invoiceEdit.invoiceid + "/GenUBLInvoice", token)
                        .then((f) => {
                            if (f) {
                                setUBLFile(f);
                                setFilePreview(f);
                            }
                            setUBLGeneration(false);
                        })
                        .catch((e) => {
                            console.log(e);
                            setUBLGeneration(false);
                        });
                }
            })
            .catch((e) => {
                console.log(e);
                setUBLGeneration(false);
            });
    };
    const ValidateUBL = async () => {
        setUBLGeneration(true);
        const token = await TokenHelper.GetToken(instance, teamsState);
        if (token !== null && invoiceEdit && invoiceEdit.invoiceid) {
            const res = await DigitealValidateDocument(token, invoiceEdit.invoiceid);
            setUBLValidate(res);
        }
        setUBLGeneration(false);
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={styles.DialogContentWithFilePreview}>
            <DialogBody className={styles.fluid}>
                <DialogTitle>
                    {
                        invoiceEdit && invoiceEdit.customerid !== null
                            ? (isCreditNote
                                ? "Edit credit note "
                                : "Edit invoice ") + invoiceEdit.invoiceRef
                            : (isCreditNote
                                ? t('CreateNewPeppolCreditNote', { consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?", interpolation: { escapeValue: false } })
                                : t('CreateNewPeppolInvoice', { consultant: consultant ? (consultant.companyName ?? consultant.displayName) : "?", interpolation: { escapeValue: false } })
                            )
                    }
                </DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {(customersLoading || projectsLoading || projectsAssigmentsLoading || consultantsLoading || salesLoading)
                            ?
                            < div className={styles.InfoPart} style={{
                                display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, maxWidth: "unset"
                            }}>
                                <div className={styles.ContentBoxBrandBackground} style={{
                                    flexGrow: "1"
                                }}>
                                    <Spinner size="large" label={t('Loading')} />
                                </div>
                            </div>
                            :
                            <>
                                {filePreview &&
                                    <FileViewer file={filePreview} />
                                }
                                {!filePreview &&
                                    < div className={styles.InfoPart} style={{
                                        display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, maxWidth: "unset", flexGrow: "1"
                                    }}>
                                        {timeSheetsEdition && timeSheetsEdition.length > 0 && (customer?.customerid?.toLowerCase() === "d4dd4973-98ca-4df0-9418-59be1452bf87" || IsNeomyticInvoice) &&
                                            <div className={styles.ContentBoxBrandBackground} style={{ gap: tokens.spacingHorizontalXS }}>
                                                <Subtitle1>Timesheets</Subtitle1>

                                                {timeSheetsEdition.map((ts) => <InvoiceLine_ts key={ts.ts.timesheetEntryid} line={ts.ts} allowEdition={!iSReadOnly} isSelected={ts.isSelected} onSelectionChange={tsSelectionChange} isNeomytic={IsNeomyticInvoice} isCreditNote={isCreditNote} />)}
                                            </div>
                                        }
                                        {salesConcat && salesConcat.length > 0 &&
                                            <div className={styles.ContentBoxBrandBackground} style={{ gap: tokens.spacingHorizontalXS }}>
                                                <Subtitle1>Sales</Subtitle1>
                                                {salesConcat.map((ts) => <InvoiceLine_sale key={ts.sale.sellid} line={ts.sale} allowEdition={!iSReadOnly} isSelected={ts.isSelected} onSelectionChange={saleSelectionChange} />)}
                                            </div>
                                        }
                                        {(!iSReadOnly || invoiceEntries.length > 0) &&
                                            <div className={styles.ContentBoxBrandBackground} style={{ gap: tokens.spacingHorizontalXS }}>
                                                <div style={{ display: 'flex', flexDirection: "row", justifyContent: "space-between" }}>
                                                    <Subtitle1>Free lines</Subtitle1>
                                                    {!iSReadOnly &&
                                                        <Button
                                                            icon={<AddIcon />}
                                                            size="small"
                                                            shape="circular"
                                                            aria-label={t('Add')}
                                                            title={t('Add')}
                                                            onClick={() => {
                                                                let id = uuidv4();
                                                                while (invoiceEntries.find(s => s.invoiceEntryid === id) !== undefined) {
                                                                    id = uuidv4();
                                                                }
                                                                let n = 0;
                                                                while (invoiceEntries.find(s => s.orderNumber === n) !== undefined) {
                                                                    n = n + 1;
                                                                }
                                                                setInvoiceEntries(
                                                                    prev => [...prev, { invoiceEntryid: id, invoiceid: NIL_UUID, sellPrice: 0, costPrice: 0, unitCode: "F", count: 1, vatcode: (IsCustomerExemptFromTax || IsCustomerIntraCommunityTax ? 0 : 21), description: null, buyerItemIdentification: null, sellerItemIdentification: null, id: n, orderNumber: n }]
                                                                );
                                                            }}
                                                        />
                                                    }
                                                </div>
                                                <DndContext
                                                    sensors={sensors}
                                                    collisionDetection={closestCenter}
                                                    onDragStart={handleDragStart}
                                                    onDragEnd={handleDragEnd}
                                                    onDragCancel={handleDragCancel}
                                                >
                                                    <SortableContext items={invoiceEntries} strategy={rectSortingStrategy}>
                                                        <div style={{ display: 'flex', flexDirection: "column", gap: tokens.spacingHorizontalS, justifyContent: "space-between" }}>
                                                            {invoiceEntries.map((ts, index) =>
                                                                <InvoiceLine_free key={ts.invoiceEntryid}
                                                                    line={ts}
                                                                    onChange={updateFreeLine}
                                                                    onDelete={deleteFreeLine}
                                                                    allowEdition={!iSReadOnly}
                                                                    isNeomytic={IsNeomyticInvoice}
                                                                    NoVATDisplay={IsCustomerExemptFromTax || IsCustomerIntraCommunityTax}
                                                                />
                                                            )}
                                                        </div>
                                                    </SortableContext>
                                                </DndContext>
                                            </div>
                                        }
                                        <div className={styles.ContentBoxBrandBackground} style={{
                                            flexGrow: "1"
                                        }}>
                                        </div>
                                    </div>
                                }
                            </>
                        }
                        <div className={styles.InfoPart} style={{
                            display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM
                        }}>
                            <div className={styles.ContentBoxBrandBackground}>
                                <div style={{ display: "flex", gap: tokens.spacingHorizontalS, justifyContent: "space-between" }}>
                                    <div>
                                        <Button
                                            icon={<TaskListLtrIcon />}
                                            shape="circular"
                                            aria-label={"View Line List"}
                                            title={"View Line List"}
                                            disabled={!file}
                                            onClick={() => {
                                                setFilePreview(undefined);
                                            }}
                                        />
                                        <Button
                                            icon={<DocumentPdfIcon />}
                                            shape="circular"
                                            aria-label={"View PDF"}
                                            title={"View PDF"}
                                            disabled={!file}
                                            onClick={() => {
                                                if (file) {
                                                    if (filePreview !== file) {
                                                        setFilePreview(file);
                                                    }
                                                    else {
                                                        setFilePreview(undefined);
                                                    }
                                                }
                                            }}
                                        />
                                        <Button
                                            icon={<CodeBlockIcon />}
                                            shape="circular"
                                            aria-label={"View UBL"}
                                            title={"View UBL"}
                                            disabled={!UBLfile}
                                            onClick={() => {
                                                setFilePreview(UBLfile);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Button
                                            icon={<GlobeShieldIcon />}
                                            shape="circular"
                                            aria-label={"Validate UBL"}
                                            title={"Validate UBL"}
                                            disabled={UBLGeneration}
                                            onClick={ValidateUBL}
                                        />
                                    </div>
                                    <div>
                                        <Button
                                            icon={<GlobeAddIcon />}
                                            shape="circular"
                                            aria-label={"Generate UBL"}
                                            title={"Generate UBL"}
                                            disabled={UBLGeneration}
                                            onClick={GenUBL}
                                        />
                                    </div>
                                </div>
                            </div>

                            {IsCustomerExemptFromTax &&
                                <MessageBar intent="info">
                                    <MessageBarBody>
                                        <MessageBarTitle>{t('VATExempt')}</MessageBarTitle>
                                    </MessageBarBody>
                                </MessageBar>
                            }
                            {IsCustomerIntraCommunityTax &&
                                <MessageBar intent="info">
                                    <MessageBarBody>
                                        <MessageBarTitle>{t('IntraComInvoice')}</MessageBarTitle>
                                    </MessageBarBody>
                                </MessageBar>
                            }

                            <div className={styles.ContentBoxBrandBackground}>
                                <Field label={isCreditNote ? t('CreditNoteRef') : t('InvoiceRef')}
                                    required
                                    validationMessage={isInvoiceRefValid ? undefined : "Invoice Ref"}
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setInvoiceRef(data.value)}
                                        value={invoiceRef !== null ? invoiceRef : ""}
                                        maxLength={50}
                                        disabled={iSReadOnly}
                                        ref={invoiceRefInputRef}
                                        style={iSReadOnly ? {
                                            color: tokens.colorNeutralForeground1, // text color 
                                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                                        } : {}}
                                    />
                                </Field>
                                <Field label={t('Customer')} className={styles.fluid} required>
                                    <CustomerDropdown currentCustomer={customer ?? undefined}
                                        customers={customers ?? []}
                                        setCustomer={setCustomer}
                                        disabled={iSReadOnly}
                                        ForceLoggedInConsultant={!IsNeomyticInvoice}
                                    />
                                </Field>
                                <Field label={"Buyer Reference/Order Reference"}
                                    required
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setBuyerReference(data.value)}
                                        value={buyerReference !== null ? buyerReference : ""}
                                        disabled={iSReadOnly}
                                        style={iSReadOnly ? {
                                            color: tokens.colorNeutralForeground1, // text color 
                                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                                        } : {}}
                                    />
                                </Field>
                                <Field label="Date" className={styles.fluid}
                                    required
                                    validationMessage={isInvoiceRefValid ? undefined : "Date not valid"}
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setDate(date === undefined ? null : date); }}
                                        value={date === null ? undefined : date}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectInvoiceDate')}
                                        disabled={iSReadOnly}
                                        style={iSReadOnly ? {
                                            color: tokens.colorNeutralForeground1, // text color 
                                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                                        } : {}}
                                    />
                                </Field>
                                {viewDebugInfo &&
                                    <Field label={t('VAT')} className={styles.fluid}>
                                        <VATDropdown
                                            currentVAT={vatCode}
                                            setVatCode={setVatCode}
                                            disabled={iSReadOnly}
                                        />
                                    </Field>
                                }
                                <Field label="Description">
                                    <Textarea
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setDescription(data.value)}
                                        value={description !== null ? description : ""}
                                        resize="vertical"
                                        disabled={iSReadOnly}
                                        style={iSReadOnly ? {
                                            color: tokens.colorNeutralForeground1, // text color 
                                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                                        } : {}}
                                    />
                                </Field>
                                <Field label={isCreditNote ? t('CreditNoteDescription') : t('InvoiceDescription')}>
                                    <Textarea
                                        className={styles.fluid}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setInvoiceDescription(data.value)}
                                        value={invoiceDescription !== null ? invoiceDescription : ""}
                                        resize="vertical"
                                        disabled={iSReadOnly}
                                        style={iSReadOnly ? {
                                            color: tokens.colorNeutralForeground1, // text color 
                                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                                        } : {}}
                                    />
                                </Field>
                                <Field label={t('PrepaidAmount') + " (" + t('IncludVAT') + ")"}>
                                    <CurrencyInput
                                        setCurrency={setPrepaidAmount}
                                        currency={prepaidAmount}
                                        digits={2}
                                        disabled={iSReadOnly}
                                        contentAfter={"\u20AC"}
                                    />
                                </Field>
                            </div>
                            <div className={styles.ContentBoxBrandBackground}>
                                <Field label={
                                    <div className={styles.spaceBetween}>
                                        <Label>{"Payment (" + t('IncludVAT') + ")"}</Label>
                                        {InvoiceTotal !== undefined && <Label>{("(" + t('DueByCustomer') + " " + InvoiceTotal.toLocaleString(undefined, { style: 'currency', currency: 'EUR' }) + ")")}</Label>}
                                    </div>
                                }
                                    className={styles.fluid}
                                >
                                    <CurrencyInput
                                        setCurrency={setPayment}
                                        currency={payment}
                                        digits={2}
                                        contentAfter={"\u20AC"}
                                    />
                                </Field>
                                <Field label="Payment Date" className={styles.fluid}>
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setPaymentDate(date === undefined ? null : date); }}
                                        value={paymentDate === null ? undefined : paymentDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectPaymentDate')}
                                    />
                                </Field>
                                {isAdmin &&
                                    <Switch label="is Locked"
                                        checked={lock}
                                        onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setLock(data.checked); }} />
                                }
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestInvoiceEdition(null))}
                    >
                        {t("Close")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface >
    </Dialog >;
};
